<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'

const props = defineProps<{
  state: string
  reported: number
  isPolling: boolean
}>()

const message = computed(() =>
  props.state === 'QUEUED' ? 'Waiting for a slot…' : `Scanning… ${props.reported} checks reported`,
)

// The visible count updates every poll, roughly once a second. A live region
// announcing on that schedule would read a number aloud once a second, which
// is worse than staying silent, so the announced copy only catches up to the
// real count every four seconds.
const announcedReported = ref(props.reported)
const announcedMessage = computed(() =>
  props.state === 'QUEUED' ? 'Waiting for a slot…' : `Scanning… ${announcedReported.value} checks reported`,
)

// Started here rather than in onMounted: `<script setup>` runs once per
// instance, same as onMounted would, without needing a guard for a timer that
// onUnmounted can otherwise always assume is set.
const timer = setInterval(() => {
  announcedReported.value = props.reported
}, 4000)

onUnmounted(() => {
  clearInterval(timer)
})
</script>

<template>
  <div
    role="status"
    aria-live="off"
    :aria-busy="props.isPolling"
    class="flex flex-col gap-2"
  >
    <div class="h-0.5 w-full overflow-hidden rounded-full bg-sunken">
      <div class="scan-sweep h-full w-1/3 rounded-full bg-accent" />
    </div>
    <p class="text-sm text-ink-muted">
      {{ message }}
    </p>
    <span
      class="sr-only"
      aria-live="polite"
    >{{ announcedMessage }}</span>
  </div>
</template>
